/**
 * What a row is doing, in words — read off the stage and activity the API stores since
 * CURR-27. This used to infer the turn from counts two of the turns never move.
 */

import type { GenerationRow, RowStage } from '@platform/schema/generation';

export const STAGE: Record<RowStage, string> = {
  READING_PLAN: 'reading the plan',
  WRITING_SCENES: 'writing scenes',
  ILLUSTRATING: 'illustrating',
  REVIEWING: 'reviewing',
  REVISING: 'repairing',
  RECHECKING: 'second review',
  SAVING: 'saving',
  READING_CONTENT: 'reading the lesson',
  IDENTIFYING_CLAIMS: 'identifying claims',
  ACCESSING_SOURCES: 'opening sources',
  COMPARING_EVIDENCE: 'comparing with sources',
  CHECKING_QUALITY: 'checking quality',
  PREPARING_FINDINGS: 'preparing findings',
};

type Progress = Pick<
  GenerationRow,
  'stage' | 'activity' | 'activityReason' | 'lessonTitle' | 'writing'
>;

/** Undefined for a row that is not moving: only an unsettled row has an activity. */
export function stageOf(row: Progress): string | undefined {
  if (row.activity === undefined) return undefined;
  if (row.activity === 'BLOCKED')
    return `blocked: ${row.activityReason ?? 'waiting'}`;

  const stage = row.stage === undefined ? 'starting' : STAGE[row.stage];
  const lesson = row.lessonTitle ?? row.writing;
  const doing = lesson === undefined ? stage : `${stage} for ${lesson}`;

  return row.activity === 'RETRYING' ? `retrying: ${doing}` : doing;
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;

/**
 * Coarse on purpose: this answers whether a row is still moving, and seconds stop helping
 * once the answer is measured in hours.
 */
export function formatDuration(ms: number): string {
  const total = Math.max(0, ms);

  if (total >= HOUR) {
    const hours = Math.floor(total / HOUR);
    const minutes = Math.floor((total % HOUR) / MINUTE);

    return `${String(hours)}h ${String(minutes)}m`;
  }

  const seconds = Math.floor(total / SECOND);

  return seconds < 60
    ? `${String(seconds)}s`
    : `${String(Math.floor(seconds / 60))}m ${String(seconds % 60)}s`;
}

/**
 * Measured to the finish where there is one, and to the caller's clock where there is not.
 *
 * That is what lets a settled row keep a correct duration after its screen has stopped
 * ticking: there is nothing left to measure against the clock.
 */
export function elapsedOf(
  row: Pick<GenerationRow, 'startedAt' | 'finishedAt'>,
  now: number,
): string | undefined {
  if (row.startedAt === undefined) return undefined;

  const end = row.finishedAt === undefined ? now : Date.parse(row.finishedAt);

  return formatDuration(end - Date.parse(row.startedAt));
}
